import React, { useEffect, useMemo, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, PencilSimple, Play, DownloadSimple, FloppyDisk, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const NUMERIC_HINTS = ["total", "subtotal", "tax", "paid", "balance", "amount", "pph23_withheld"];

export default function CustomReports() {
  const [catalog, setCatalog] = useState({ sources: [] });
  const [saved, setSaved] = useState([]);
  const [search, setSearch] = useState("");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const empty = { name: "", description: "", source: "sales_invoices", columns: [], filters: {}, group_by: "", aggregation: "", aggregate_field: "" };
  const [form, setForm] = useState(empty);

  const load = () => {
    api.get("/custom-reports/catalog").then(r => setCatalog(r.data));
    api.get("/custom-reports").then(r => setSaved(r.data));
  };
  useEffect(() => { load(); }, []);

  const activeSource = useMemo(() => catalog.sources.find(s => s.key === form.source) || { columns: [], numeric_fields: [] }, [catalog, form.source]);

  const openNew = () => { setEditingId(null); setForm({ ...empty, columns: [] }); setResult(null); setDlgOpen(true); };
  const openEdit = (r) => { setEditingId(r.id); setForm({ ...empty, ...r, filters: r.filters || {}, columns: r.columns || [] }); setResult(null); setDlgOpen(true); };

  const toggleCol = (c) => {
    const cur = new Set(form.columns || []);
    if (cur.has(c)) cur.delete(c); else cur.add(c);
    setForm({ ...form, columns: Array.from(cur) });
  };

  const runPreview = async () => {
    setRunning(true);
    try {
      const payload = { ...form, columns: form.columns.length ? form.columns : activeSource.columns };
      const r = await api.post("/custom-reports/run", payload);
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal jalankan"); }
    finally { setRunning(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama laporan wajib diisi");
    try {
      const payload = { ...form, columns: form.columns.length ? form.columns : activeSource.columns };
      if (editingId) await api.put(`/custom-reports/${editingId}`, payload);
      else await api.post("/custom-reports", payload);
      toast.success(editingId ? "Perubahan tersimpan" : "Laporan tersimpan");
      setDlgOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  const runSaved = async (r) => {
    setRunning(true);
    try { const resp = await api.get(`/custom-reports/${r.id}/run`); setResult({ ...resp.data, __name: r.name }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setRunning(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Hapus laporan "${r.name}"?`)) return;
    await api.delete(`/custom-reports/${r.id}`);
    toast.success("Terhapus"); load();
  };

  const exportCsv = () => {
    if (!result) return;
    const rows = [result.columns.join(","), ...result.rows.map(r => result.columns.map(c => JSON.stringify(r[c] ?? "")).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${(result.__name || "laporan").replace(/\s+/g, "_")}.csv`; a.click();
  };

  const filteredSaved = saved.filter(r => !search.trim() || [r.name, r.description, r.source].some(v => (v || "").toLowerCase().includes(search.toLowerCase())));

  const fmtCell = (col, val) => {
    if (val == null || val === "") return "—";
    if (NUMERIC_HINTS.some(h => col.toLowerCase().includes(h)) || col.startsWith("sum_") || col.startsWith("avg_")) return formatIDR(val);
    if (col === "date" || col === "due_date") return formatDate(val);
    return String(val);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="custom-reports-page">
      <div className="flex justify-between items-baseline">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Laporan Custom</h1>
          <p className="text-sm text-slate-500 mt-1">Referensi cepat: Neraca Saldo, Laba Rugi, Neraca, SPT PPN ada di menu Laporan. Di sini kamu bisa merancang laporan sendiri (filter, kolom, group by, agregasi) dan menyimpannya untuk dipakai ulang.</p>
        </div>
        <Button className="bg-amber-600 hover:bg-amber-700" onClick={openNew} data-testid="new-custom-report-btn"><Plus size={16} className="mr-1" />Laporan Baru</Button>
      </div>

      <div className="mt-6 mb-3 relative w-full max-w-md">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari laporan…" className="pl-8 h-9" data-testid="custom-reports-search" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md" data-testid="custom-reports-table">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Sumber</TableHead><TableHead>Deskripsi</TableHead><TableHead>Dibuat</TableHead><TableHead className="w-40"></TableHead></TableRow></TableHeader>
          <TableBody>
            {filteredSaved.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-500 text-sm">{search ? "Tidak ada hasil yang cocok." : "Belum ada laporan tersimpan. Klik Laporan Baru untuk mulai."}</TableCell></TableRow> : filteredSaved.map(r => (
              <TableRow key={r.id} data-testid={`saved-report-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="font-mono-plex text-xs text-slate-500">{r.source}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.description || "—"}</TableCell>
                <TableCell className="text-xs">{formatDate(r.created_at)}</TableCell>
                <TableCell><div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => runSaved(r)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`run-${r.id}`}><Play size={12} className="mr-1" />Jalankan</Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(r)}><PencilSimple size={12} /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(r)} className="border-rose-300 text-rose-700 hover:bg-rose-50"><Trash size={12} /></Button>
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {result && (
        <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="report-result">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-serif-display text-xl font-medium">{result.__name || "Pratinjau Hasil"}</h3>
              <div className="text-xs text-slate-500">{result.meta?.__total_rows || 0} baris</div>
            </div>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv-btn"><DownloadSimple size={14} className="mr-1" />Unduh CSV</Button>
          </div>
          <div className="overflow-auto max-h-[500px] border border-slate-200 rounded">
            <Table>
              <TableHeader><TableRow>{result.columns.map(c => <TableHead key={c} className="capitalize">{c.replace(/_/g, " ")}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {result.rows.length === 0 ? <TableRow><TableCell colSpan={result.columns.length} className="text-center py-10 text-slate-500 text-sm">Tidak ada data untuk filter ini.</TableCell></TableRow> :
                  result.rows.map((r, i) => <TableRow key={i}>{result.columns.map(c => <TableCell key={c} className={NUMERIC_HINTS.some(h => c.toLowerCase().includes(h)) || c.startsWith("sum_") || c.startsWith("avg_") ? "tabular-nums text-right" : ""}>{fmtCell(c, r[c])}</TableCell>)}</TableRow>)}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit Laporan Custom" : "Laporan Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nama Laporan</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="cth: Penjualan Bulanan per Pelanggan" data-testid="cr-name-input" /></div>
              <div><Label>Sumber Data</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v, columns: [], group_by: "", aggregate_field: "" })}>
                  <SelectTrigger data-testid="cr-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{catalog.sources.map(s => <SelectItem key={s.key} value={s.key}>{s.key}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Deskripsi</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

            <div>
              <Label>Kolom yang ditampilkan <span className="text-slate-400 text-[11px] font-normal">(kosong = semua)</span></Label>
              <div className="mt-1 flex flex-wrap gap-1.5 bg-slate-50 border border-slate-200 rounded p-2">
                {activeSource.columns.map(c => {
                  const checked = form.columns.includes(c);
                  return <label key={c} className={`text-xs px-2 py-1 rounded cursor-pointer border ${checked ? "bg-amber-100 border-amber-300 text-amber-800" : "bg-white border-slate-200"}`}><input type="checkbox" checked={checked} onChange={() => toggleCol(c)} className="hidden" data-testid={`cr-col-${c}`} />{c}</label>;
                })}
              </div>
            </div>

            <div>
              <Label>Filter</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                <Input type="date" placeholder="Dari" value={form.filters?.date_from || ""} onChange={(e) => setForm({ ...form, filters: { ...form.filters, date_from: e.target.value } })} data-testid="cr-date-from" />
                <Input type="date" placeholder="Sampai" value={form.filters?.date_to || ""} onChange={(e) => setForm({ ...form, filters: { ...form.filters, date_to: e.target.value } })} data-testid="cr-date-to" />
                <Input placeholder="Partner (contains)" value={form.filters?.partner_name || ""} onChange={(e) => setForm({ ...form, filters: { ...form.filters, partner_name: e.target.value } })} />
                <Input placeholder="Status" value={form.filters?.status || ""} onChange={(e) => setForm({ ...form, filters: { ...form.filters, status: e.target.value } })} />
                <Input placeholder="Proyek (contains)" value={form.filters?.project_name || ""} onChange={(e) => setForm({ ...form, filters: { ...form.filters, project_name: e.target.value } })} />
                <div className="grid grid-cols-2 gap-1">
                  <Input type="number" placeholder="Total min" value={form.filters?.min_total || ""} onChange={(e) => setForm({ ...form, filters: { ...form.filters, min_total: e.target.value } })} />
                  <Input type="number" placeholder="Total max" value={form.filters?.max_total || ""} onChange={(e) => setForm({ ...form, filters: { ...form.filters, max_total: e.target.value } })} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Group By</Label>
                <Select value={form.group_by || "__none__"} onValueChange={(v) => setForm({ ...form, group_by: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="cr-groupby"><SelectValue placeholder="Tanpa grouping" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— (tanpa) —</SelectItem>
                    <SelectItem value="month">Bulan (YYYY-MM)</SelectItem>
                    {activeSource.columns.filter(c => !NUMERIC_HINTS.some(h => c.toLowerCase().includes(h))).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Agregasi</Label>
                <Select value={form.aggregation || "__none__"} onValueChange={(v) => setForm({ ...form, aggregation: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="cr-agg"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— (tanpa) —</SelectItem>
                    <SelectItem value="sum">Jumlah (SUM)</SelectItem>
                    <SelectItem value="avg">Rata-rata (AVG)</SelectItem>
                    <SelectItem value="count">Cacah (COUNT)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Field yang diagregasi</Label>
                <Select value={form.aggregate_field || "__none__"} onValueChange={(v) => setForm({ ...form, aggregate_field: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— (default: total/amount) —</SelectItem>
                    {(activeSource.numeric_fields || []).map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-between gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={runPreview} disabled={running} data-testid="cr-preview-btn"><Play size={14} className="mr-1" />{running ? "Menjalankan…" : "Pratinjau"}</Button>
              <Button className="bg-slate-900" onClick={save} data-testid="cr-save-btn"><FloppyDisk size={14} className="mr-1" />{editingId ? "Simpan Perubahan" : "Simpan Laporan"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
